import { useEffect, useSyncExternalStore } from 'react';
import { brandConfig } from '../config/brands';
import {
  applyAuthScheme,
  applyLoginBgImage,
  getAuthScheme,
} from '../config/brands/applyBrandTheme';
import { apiClient } from '../services/apiClient';

/** <html data-auth-scheme> の変更を購読する。属性は applyBrandTheme() (起動時) と
 *  AuthGate の /partner/login-branding 応答後 (applyAuthScheme) に更新されるため、
 *  マウント後に応答が届いてもロゴ等の JS 分岐が追従できるようにする。 */
function subscribe(onChange: () => void): () => void {
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['data-auth-scheme'],
  });
  return () => observer.disconnect();
}

/** 認証系画面 (ログイン / 新規登録 / オンボーディング〜組織設定) の現在の配色を
 *  返すフック。優先順位: partners.onboarding_color_scheme > BrandConfig.colorScheme > 'dark'。
 *  auth-pages.css 系は `:root[data-auth-scheme]` で CSS が自動で切り替わるため、
 *  このフックはロゴ画像の選択や module CSS の `.light` クラス付与など、
 *  レンダリング分岐が必要な箇所でのみ使う。 */
export function useAuthScheme(): 'light' | 'dark' {
  return useSyncExternalStore(subscribe, getAuthScheme, () => 'dark' as const);
}

/** AuthGate の外で直接開かれる認証前ページ (新規登録 / パスワードリセット /
 *  招待受諾 等) 用: パートナーの配色・ログイン背景画像を /partner/login-branding
 *  から取得して適用する。AuthGate 配下ではフェッチ済みのため不要だが、
 *  招待メールからの直リンクなどキャッシュが無い初回訪問で
 *  ブランド既定に落ちるのを防ぐ。 */
export function useAuthBrandingSync(): void {
  useEffect(() => {
    apiClient
      .get<{
        login_bg_image_url: string | null;
        onboarding_color_scheme?: 'light' | 'dark' | null;
      }>(`/partner/login-branding?partner_code=${brandConfig.id}`)
      .then(({ data }) => {
        applyAuthScheme(data.onboarding_color_scheme ?? null);
        applyLoginBgImage(data.login_bg_image_url ?? null, brandConfig.id);
      })
      .catch(() => {/* ネットワークエラー時はキャッシュ値 or ブランド既定で表示 */});
  }, []);
}

/** 認証系画面のブランドロゴ。ライトモードでは明色背景向けロゴ (logoDark) を
 *  使い、未定義ブランドは白ロゴを CSS invert で反転する (.onboarding-logo--invert)。 */
export function useAuthLogo(): { src: string; invert: boolean } {
  const scheme = useAuthScheme();
  if (scheme === 'light') {
    return brandConfig.logoDark
      ? { src: brandConfig.logoDark, invert: false }
      : { src: brandConfig.logoWhite, invert: true };
  }
  return { src: brandConfig.logoWhite, invert: false };
}
